import type { Papel } from '@/nucleo/auth/papeis';
import { useAuth } from '@/nucleo/auth/useAuth';

/** Titular e administrador convidado; ver `isAdminCred` abaixo. */
const PAPEIS_QUE_ADMINISTRAM_A_EQUIPE: readonly Papel[] = ['CREDENCIADO', 'ADMIN_CREDENCIADO'];

/**
 * Fronteira PF × coletivo (PJ) × cooperativa × administrador dentro do Portal
 * do Transportador.
 *
 * Fonte real: `usuario.credenciadoTipo`, que vem do `GET /eu`/BFF de login,
 * `apps/api/src/nucleo/auth/usuario.service.ts` resolve via
 * `usuario_tipo_credenciado()` (`db/migrations/0112`), consultando
 * `credenciado_operadores`/`credenciados.tipo`. Antes desta função a
 * aplicação não tinha como saber o tipo do credenciado logado, por isso
 * `condutores/contrato.ts` e `cooperados/contrato.ts` registravam a ausência
 * de recorte como decisão, não como esquecimento.
 *
 * Mesma regra do protótipo (`usePortalAccess.ts`):
 * - `isColetivo` = PJ ou COOPERATIVA; `isCoop` = COOPERATIVA.
 *
 * O `isAdmin` do protótipo (ADMIN, GESTOR_ADMIN ou GESTOR passando por toda
 * guarda) não existe aqui: papel interno não entra no Portal do Credenciado
 * (decisão do dono em 2026-10-02; `AREA_CREDENCIADO.papeis` em `mapa.ts`).
 *
 * `isAdminCred` diverge do protótipo por decisão do dono em 2026-10-01: quem
 * administra a equipe é o credenciado coletivo (PJ ou Cooperativa), pelo
 * titular (`CREDENCIADO`) ou pelo administrador convidado
 * (`ADMIN_CREDENCIADO`); PF não tem equipe e não entra. No protótipo o
 * titular só recebe `CREDENCIADO` e nunca via a tela — só as personas de
 * homologação, que nasciam com `ADMIN_CREDENCIADO`. Aqui `CREDENCIADO` é
 * sempre o titular: o convite do admin emite só os três papéis delegados
 * (`admin-usuarios.service.ts`). O perfil `ADMIN_CRED` de
 * `credenciado_colaboradores` não conta porque nenhuma rota lê a tabela.
 *
 * Usada pela guarda de tela (`PortalAccessGuard`), por `FrotaEquipePage` e
 * por `EquipePage`.
 */
export function usePortalAccess() {
  const { usuario } = useAuth();
  const tipo = usuario?.credenciadoTipo ?? null;
  const papeis = usuario?.papeis ?? [];
  const isColetivo = tipo === 'PJ' || tipo === 'COOPERATIVA';

  return {
    tipo,
    isColetivo,
    isCoop: tipo === 'COOPERATIVA',
    isAdminCred:
      isColetivo && papeis.some((papel) => PAPEIS_QUE_ADMINISTRAM_A_EQUIPE.includes(papel)),
  };
}
